/**
 * Split-screen layout utilities for multi-client canvas grid layouts.
 * Provides aspect-ratio-aware grid calculations and DOM setup.
 */

export interface SplitScreenGridConfig {
  cols: number;
  rows: number;
  cellWidth: number;
  cellHeight: number;
}

export interface SplitScreenOptions {
  /** The total number of clients/canvases to fit */
  count: number;
  /** Container width (defaults to window.innerWidth) */
  containerWidth?: number;
  /** Container height (defaults to window.innerHeight) */
  containerHeight?: number;
  /** Aspect ratio for cells, defaults to 16:9 (640/360) */
  aspectRatio?: number;
  /** Minimum canvas resolution, defaults to 300 */
  minCanvasSize?: number;
  /** `'grid'` (default) splits whichever axis keeps cells nearest `aspectRatio`; `'stack'` fills rows first. */
  arrangement?: 'grid' | 'stack';
}

/**
 * Calculate optimal grid layout for split-screen display.
 * Uses aspect-ratio-aware algorithm to determine rows and columns.
 */
export function calculateSplitScreenGrid(options: SplitScreenOptions): SplitScreenGridConfig {
  const {
    count,
    containerWidth = typeof window !== 'undefined' ? window.innerWidth : 800,
    containerHeight = typeof window !== 'undefined' ? window.innerHeight : 600,
    aspectRatio = 640 / 360, // 16:9
    minCanvasSize = 300,
    arrangement = 'grid',
  } = options;

  if (count <= 0) {
    return { cols: 1, rows: 1, cellWidth: containerWidth, cellHeight: containerHeight };
  }

  if (arrangement === 'stack') {
    // Rows first, so two clients stack top/bottom (each viewport keeps the full width).
    const rows = Math.ceil(Math.sqrt(count));
    const cols = Math.ceil(count / rows);
    return { cols, rows, cellWidth: Math.max(Math.floor(containerWidth / cols), minCanvasSize), cellHeight: Math.max(Math.floor(containerHeight / rows), minCanvasSize) };
  }

  let cellWidth = containerWidth;
  let cellHeight = containerHeight;
  let cols = 1;
  let rows = 1;

  while (cols * rows < count) {
    if (cellWidth > cellHeight * aspectRatio) {
      cols++;
      cellWidth = containerWidth / cols;
    } else {
      rows++;
      cellHeight = containerHeight / rows;
    }
  }

  // Ensure minimum canvas resolution
  cellWidth = Math.max(Math.floor(cellWidth), minCanvasSize);
  cellHeight = Math.max(Math.floor(cellHeight), minCanvasSize);

  return { cols, rows, cellWidth, cellHeight };
}

export interface ApplySplitScreenLayoutOptions {
  /** The container element to apply grid layout to */
  container: HTMLElement;
  /** Grid configuration from calculateSplitScreenGrid */
  grid: SplitScreenGridConfig;
}

/**
 * Apply CSS grid layout to a container for split-screen display.
 */
export function applySplitScreenLayout(options: ApplySplitScreenLayoutOptions): void {
  const { container, grid } = options;

  container.style.display = 'grid';
  container.style.gridTemplateColumns = `repeat(${grid.cols}, 1fr)`;
  container.style.gridTemplateRows = `repeat(${grid.rows}, 1fr)`;
  container.style.gap = '0px';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.margin = '0';
  container.style.padding = '0';
  container.style.position = 'relative';
}

export interface CreateCanvasWrapperOptions {
  /** Canvas index for ID generation */
  index: number;
  /** ID prefix for the canvas (default: 'canvas') */
  idPrefix?: string;
  /** CSS class for the canvas (default: 'split-screen-canvas') */
  canvasClass?: string;
  /** CSS class for the wrapper (default: 'split-screen-canvas-wrapper') */
  wrapperClass?: string;
  /** Background color for wrapper (default: '#1a1a2e') */
  backgroundColor?: string;
  /** Border style for canvas (default: '1px solid #3c3c3c') */
  borderStyle?: string;
  /** Canvas width in pixels */
  canvasWidth?: number;
  /** Canvas height in pixels */
  canvasHeight?: number;
}

export interface CanvasWrapperResult {
  wrapper: HTMLDivElement;
  canvas: HTMLCanvasElement;
}

/**
 * Create a canvas wrapper div with proper styling for split-screen layout.
 */
export function createCanvasWrapper(options: CreateCanvasWrapperOptions): CanvasWrapperResult {
  const {
    index,
    idPrefix = 'canvas',
    canvasClass = 'split-screen-canvas',
    wrapperClass = 'split-screen-canvas-wrapper',
    backgroundColor = '#1a1a2e',
    borderStyle = '1px solid #3c3c3c',
    canvasWidth,
    canvasHeight,
  } = options;

  const wrapper = document.createElement('div');
  wrapper.className = wrapperClass;
  wrapper.style.background = backgroundColor;
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';
  wrapper.style.border = 'none';
  wrapper.style.width = '100%';
  wrapper.style.height = '100%';
  wrapper.style.overflow = 'hidden';
  wrapper.style.maxWidth = '100%';
  wrapper.style.maxHeight = '100%';

  const canvas = document.createElement('canvas');
  canvas.id = `${idPrefix}${index}`;
  canvas.className = canvasClass;
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.maxWidth = '100%';
  canvas.style.maxHeight = '100%';
  canvas.style.border = borderStyle;

  if (canvasWidth !== undefined) {
    canvas.width = canvasWidth;
  }
  if (canvasHeight !== undefined) {
    canvas.height = canvasHeight;
  }

  wrapper.appendChild(canvas);

  return { wrapper, canvas };
}

export interface SetupSplitScreenOptions {
  /** The container element */
  container: HTMLElement;
  /** Number of canvases to create */
  count: number;
  /** Aspect ratio for cells, defaults to 16:9 */
  aspectRatio?: number;
  /** Minimum canvas size, defaults to 300 */
  minCanvasSize?: number;
  /** How the cells are tiled: `'grid'` (default, aspect-aware) or `'stack'` (rows first). */
  arrangement?: 'grid' | 'stack';
  /** ID prefix for canvases */
  idPrefix?: string;
  /** CSS class for canvases */
  canvasClass?: string;
  /** CSS class for wrappers */
  wrapperClass?: string;
  /** Background color for wrappers */
  backgroundColor?: string;
  /** Border style for canvases */
  borderStyle?: string;
}

export interface SplitScreenSetupResult {
  grid: SplitScreenGridConfig;
  canvases: HTMLCanvasElement[];
  wrappers: HTMLDivElement[];
}

/**
 * Full setup for split-screen multi-canvas layout.
 * Calculates grid, applies CSS layout, and creates all canvas wrappers.
 */
export function setupSplitScreen(options: SetupSplitScreenOptions): SplitScreenSetupResult {
  const {
    container,
    count,
    aspectRatio = 640 / 360,
    minCanvasSize = 300,
    arrangement = 'grid',
    idPrefix = 'canvas',
    canvasClass = 'split-screen-canvas',
    wrapperClass = 'split-screen-canvas-wrapper',
    backgroundColor = '#1a1a2e',
    borderStyle = '1px solid #3c3c3c',
  } = options;

  const grid = calculateSplitScreenGrid({
    count,
    containerWidth: container.clientWidth || window.innerWidth,
    containerHeight: container.clientHeight || window.innerHeight,
    aspectRatio,
    minCanvasSize,
    arrangement,
  });

  applySplitScreenLayout({ container, grid });

  const canvases: HTMLCanvasElement[] = [];
  const wrappers: HTMLDivElement[] = [];

  for (let i = 0; i < count; i++) {
    const { wrapper, canvas } = createCanvasWrapper({
      index: i,
      idPrefix,
      canvasClass,
      wrapperClass,
      backgroundColor,
      borderStyle,
      canvasWidth: grid.cellWidth,
      canvasHeight: grid.cellHeight,
    });

    wrappers.push(wrapper);
    canvases.push(canvas);
    container.appendChild(wrapper);
  }

  return { grid, canvases, wrappers };
}
